import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { CONFIG_OVERRIDE_LABELS } from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectControlled } from '#components/patterns/forms/select/select/SelectControlled.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { ConfigOverride } from '#dn-types/host.ts';
import { ON_OFF } from '#schemas/config-override/helpers/enums.ts';
import { mapFormToOverrides } from '#screens/hosts/form/advanced/mappers/mapFormToOverrides.ts';
import { schemas, type PrometheusFormData } from '../config-forms/form-data.ts';

const DEFAULT_VALUES = {
  stats: {
    type: 'prometheus' as const,
    host: '',
    prefix: '',
    listen: '',
    path: '',
    namespace: '',
    subsystem: '',
    interval: null,
    lighthouse_metrics: '' as const,
    message_metrics: '' as const,
  },
};

type FormData = PrometheusFormData;

type Props = {
  defaultValues?: FormData;
  onSave: (overrides: ConfigOverride[]) => void;
};

export function PrometheusForm({ defaultValues, onSave }: Props) {
  const { register, formState, handleSubmit, control } = useForm({
    resolver: zodResolver(schemas.prometheus),
    defaultValues: defaultValues ?? DEFAULT_VALUES,
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return handleSubmit((data) => {
      return Promise.resolve(onSave(mapFormToOverrides(data)));
    })(e);
  };

  const errors = formState.errors;

  return (
    <form method="post" id="prometheus-form" onSubmit={onSubmit}>
      <Spacer direction="vertical" size="wide">
        {/* hidden field to include stats.type in the overrides that are saved */}
        <Input hidden {...register('stats.type')} />
        <Field label={CONFIG_OVERRIDE_LABELS['stats.listen']}>
          <Input {...register('stats.listen')} className="width-100" hasError={Boolean(errors.stats?.listen)} />
          {errors.stats?.listen && <ErrorMessage>{errors.stats.listen.message?.toString()}</ErrorMessage>}
        </Field>
        <Field label={CONFIG_OVERRIDE_LABELS['stats.path']}>
          <Input {...register('stats.path')} className="width-100" hasError={Boolean(errors.stats?.path)} />
          {errors.stats?.path && <ErrorMessage>{errors.stats.path.message?.toString()}</ErrorMessage>}
        </Field>
        <Field label={CONFIG_OVERRIDE_LABELS['stats.namespace']}>
          <Input {...register('stats.namespace')} className="width-100" hasError={Boolean(errors.stats?.namespace)} />
          {errors.stats?.namespace && <ErrorMessage>{errors.stats.namespace.message?.toString()}</ErrorMessage>}
        </Field>
        <Field label={CONFIG_OVERRIDE_LABELS['stats.subsystem']}>
          <Input {...register('stats.subsystem')} className="width-100" hasError={Boolean(errors.stats?.subsystem)} />
          {errors.stats?.subsystem && <ErrorMessage>{errors.stats.subsystem.message?.toString()}</ErrorMessage>}
        </Field>

        {/* Shared fields */}
        <Field label={CONFIG_OVERRIDE_LABELS['stats.interval']}>
          <Input
            {...register('stats.interval')}
            className="width-100"
            type="text"
            max={255}
            inputMode="numeric"
            hasError={Boolean(errors.stats?.interval)}
          />
          {errors.stats?.interval && <ErrorMessage>{errors.stats.interval.message?.toString()}</ErrorMessage>}
        </Field>
        <SelectControlled
          label={CONFIG_OVERRIDE_LABELS['stats.lighthouse_metrics']}
          name="stats.lighthouse_metrics"
          control={control}
          hasError={Boolean(errors.stats?.lighthouse_metrics)}
          renderValue={(item) => item || null}
          renderOptions={({ onChange }) => (
            <>
              {ON_OFF.map((v) => (
                <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                  {v}
                </SelectItem>
              ))}
            </>
          )}
        />
        <SelectControlled
          label={CONFIG_OVERRIDE_LABELS['stats.message_metrics']}
          name="stats.message_metrics"
          control={control}
          hasError={Boolean(errors.stats?.message_metrics)}
          renderValue={(item) => item || null}
          renderOptions={({ onChange }) => (
            <>
              {ON_OFF.map((v) => (
                <SelectItem key={v} value={v} onClick={() => onChange(v)}>
                  {v}
                </SelectItem>
              ))}
            </>
          )}
        />
      </Spacer>
    </form>
  );
}
